import { Link } from "react-router-dom";
import { BadgeCheck, Star } from "lucide-react";
import { STATUS_LABEL, STATUS_TONE, mediaUrl } from "@/lib/api";

export function VerifiedBadge({ size = 16 }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" title="Terverifikasi" data-testid="verified-badge">
      <BadgeCheck size={size} strokeWidth={1.75} className="text-emerald-600" />Terverifikasi
    </span>
  );
}

export function Avatar({ src, name, size = 56, online }) {
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {src ? (
        <img src={mediaUrl(src)} alt={name} className="w-full h-full rounded-full object-cover border border-border" />
      ) : (
        <span className="w-full h-full rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-heading font-semibold" style={{ fontSize: size / 2.6 }}>{name?.[0]}</span>
      )}
      {online !== undefined && (
        <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-card ${online ? "bg-emerald-500" : "bg-slate-300"}`} data-testid="online-dot" />
      )}
    </span>
  );
}

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_TONE[status] || "bg-slate-100 text-slate-600"}`} data-testid={`status-badge-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function TalentCard({ t }) {
  return (
    <Link to={`/talent/${t.id}`} className="photo-card aspect-[4/5] block group" data-testid={`talent-card-${t.id}`}>
      {t.avatar_url ? (
        <img src={mediaUrl(t.avatar_url)} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-7xl font-heading font-semibold text-neutral-400">{t.name?.[0]}</div>
      )}
      <div className="absolute top-4 left-4 right-4 flex flex-wrap gap-2">
        {t.skills?.slice(0, 2).map((s, i) => (
          <span key={s} className="glass inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs text-neutral-900"><span className="tag-dot" style={{ background: i ? "var(--peach)" : "var(--mint)" }} />{s}</span>
        ))}
      </div>
      <div className="absolute left-4 right-4 bottom-4 glass rounded-2xl p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-heading text-xl font-semibold text-neutral-900 leading-tight">{t.name}</h3>
          <span className={`w-2.5 h-2.5 rounded-full ${t.is_online ? "bg-emerald-500" : "bg-neutral-400"}`} data-testid="online-dot" title={t.is_online ? "Online" : "Offline"} />
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-neutral-800">
          <VerifiedBadge size={13} />
          <span className="inline-flex items-center gap-1"><Star size={12} className="text-amber-500 fill-amber-400" strokeWidth={1.5} />{t.rating_avg?.toFixed(1)} ({t.rating_count})</span>
          <span>{t.is_busy ? "Sedang sesi" : t.is_online ? "Online" : "Offline"}</span>
        </div>
      </div>
    </Link>
  );
}

export function Countdown({ target, onDone, className = "" }) {
  const [left, setLeft] = useCountdown(target, onDone);
  const m = Math.floor(left / 60), s = left % 60;
  return <span className={`tabular-nums font-heading font-semibold ${className}`} data-testid="countdown">{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

import { useEffect, useState } from "react";
function useCountdown(target, onDone) {
  const calc = () => Math.max(0, Math.floor((new Date(target).getTime() - Date.now()) / 1000));
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => {
      const v = calc();
      setLeft(v);
      if (v === 0) { clearInterval(id); onDone && onDone(); }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return [left, setLeft];
}

export function Disclaimer() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-5 py-4 text-sm text-amber-900 leading-relaxed" data-testid="disclaimer-banner">
      <p className="font-medium">Sudut Nadi bukan layanan psikolog, konseling klinis, atau terapi.</p>
      <p className="mt-1">Talent adalah pendengar terlatih, bukan tenaga kesehatan. Dalam kondisi darurat hubungi 112 atau hotline Kemenkes 119 ext. 8.</p>
    </div>
  );
}
